import React from "react";

import { Switch } from "@material-ui/core";

import {
  getBenchmarkName,
  getBenchmarkColumnName,
} from "../../helpers/nameHelpers";

const maxBenchmarksAllowed = 3;

const AdditionalBenchmarks = React.memo(
  (props) => {
    const { actions, portfolio, portfolioEntry } = props;
    const { benchmarks } = portfolioEntry;
    var selectedBenchmarks = portfolio.benchmarks.filter(
      (e) => e.key !== "clientSpecified" && e.selected,
    );

    const isCAD = portfolio.currencyType === "CAD";

    const contentCellWidth = isCAD ? 120 : 120;
    const leftCellWidth = isCAD ? 150 : 250;
    const checkCellWidth = 50;

    const border = "1px solid #cac9c9";

    function getTextCell(benchmark, name, hasBorder) {
      return (
        <div
          style={{
            width: contentCellWidth,
            minWidth: contentCellWidth,
            textAlign: "center",
            paddingLeft: 5,
            paddingRight: 5,
            paddingTop: 15,
            borderLeft: hasBorder ? border : "",
          }}
        >
          {benchmark[name] ? benchmark[name] * 100 + "%" : ""}
        </div>
      );
    }

    function getBenchmarkRow(key) {
      var benchmark = benchmarks.find((e) => e.key === key);

      var benchmarkSelected = !!selectedBenchmarks.find(
        (e) => e.key === key && e.selected,
      );

      if (!benchmark) benchmark = {};

      var selectable = !(
        !benchmarkSelected &&
        (portfolio.addtionalBenchmarksBypass ||
          selectedBenchmarks.length >= maxBenchmarksAllowed)
      );

      return (
        <div
          style={{
            display: "flex",
            backgroundColor: benchmarkSelected ? "#effff0" : "",
          }}
        >
          <div
            style={{
              width: leftCellWidth,
              minWidth: leftCellWidth,
              paddingTop: 15,
              paddingLeft: 5,
              paddingRight: 5,
              textAlign: "right",
            }}
          >
            <span style={{ paddingRight: 10 }}>
              {getBenchmarkName(key, portfolio.currencyType)}
            </span>
          </div>
          <div
            style={{
              width: checkCellWidth,
              minWidth: checkCellWidth,
              padding: 5,
              borderLeft: border,
            }}
            title={
              selectable ? "" : "Maximum of 3 additional benchmarks allowed"
            }
          >
            <Switch
              disabled={!selectable}
              checked={benchmarkSelected === true}
              onChange={(event) =>
                actions.setBenchmarkValue(
                  props,
                  portfolio.portfolioNumber,
                  "selected",
                  event.target.checked,
                  benchmark.key,
                )
              }
              color="primary"
            />
          </div>
          {getTextCell(benchmark, "mainIndex", true)}
          {getTextCell(benchmark, "secondaryIndex", false)}
          {getTextCell(benchmark, "tenYearBond", false)}
          {getTextCell(benchmark, "oneMonthTbill", false)}
          {isCAD && getTextCell(benchmark, "worldDeveloped", false)}
          {getTextCell(benchmark, "goldETF", false)}
        </div>
      );
    }
    return (
      <div style={{ width: "100%", fontSize: 14 }}>
        <div style={{ display: "flex" }}>
          <div
            style={{
              width: leftCellWidth,
              minWidth: leftCellWidth,
              padding: 5,
            }}
          ></div>
          <div
            style={{
              width: checkCellWidth,
              minWidth: checkCellWidth,
              padding: 5,
              borderLeft: border,
            }}
          ></div>
          <div
            style={{
              width: contentCellWidth,
              minWidth: contentCellWidth,
              padding: 5,
              textAlign: "center",
              borderLeft: border,
            }}
          >
            {getBenchmarkColumnName("mainIndex", portfolio.currencyType)}
          </div>
          <div
            style={{
              width: contentCellWidth,
              minWidth: contentCellWidth,
              padding: 5,
              textAlign: "center",
            }}
          >
            {getBenchmarkColumnName("secondaryIndex", portfolio.currencyType)}
          </div>
          <div
            style={{
              width: contentCellWidth,
              minWidth: contentCellWidth,
              padding: 5,
              textAlign: "center",
            }}
          >
            {getBenchmarkColumnName("tenYearBond", portfolio.currencyType)}
          </div>
          <div
            style={{
              width: contentCellWidth,
              minWidth: contentCellWidth,
              padding: 5,
              textAlign: "center",
            }}
          >
            {getBenchmarkColumnName("oneMonthTbill", portfolio.currencyType)}
          </div>
          {isCAD && (
            <div
              style={{
                width: contentCellWidth,
                minWidth: contentCellWidth,
                padding: 5,
                textAlign: "center",
              }}
            >
              {getBenchmarkColumnName("worldDeveloped", portfolio.currencyType)}
            </div>
          )}
          <div
            style={{
              width: contentCellWidth,
              minWidth: contentCellWidth,
              padding: 5,
              textAlign: "center",
            }}
          >
            {getBenchmarkColumnName("goldETF", portfolio.currencyType)}
          </div>
        </div>
        {getBenchmarkRow("cdnStocks")}
        {getBenchmarkRow("usStocksUnhedged")}
        {getBenchmarkRow("cdnLongGovtBonds")}
        {getBenchmarkRow("cdnCash")}
        {isCAD && getBenchmarkRow("worldDeveloped")}
        {getBenchmarkRow("goldETF")}
        {getBenchmarkRow("balancedCdnUnhedgedUsEquity")}
        {getBenchmarkRow("balancedCdnEquityAndCdnGovtBonds")}
        {getBenchmarkRow("balancedCdnEquityAndCdnGovtBondsPlusCash")}
        {getBenchmarkRow("balancedCdnAndUsEquityAndCdnGovtBonds")}
      </div>
    );
  },
  (pp, np) => {
    return !hasChange(pp, np);
  },
);

function isComplete(props) {
  const { portfolio } = props;

  const additionalBenchmarks = portfolio.benchmarks.filter(
    (e) => e.key !== "clientSpecified" && e.selected,
  );

  return additionalBenchmarks.length <= maxBenchmarksAllowed;
}

const Title = "Benchmarks";

const Description = () => (
  <div>
    <p>
      You may select a maximum of three additional benchmarks from the list.
      Their performance results may be useful for evaluating the asset
      allocations of your portfolio.
    </p>
  </div>
);

const Preview = (props) => {
  if (!isComplete(props)) {
    return <div>Not Entered</div>;
  }

  const { portfolio, portfolioEntry } = props;

  var benchmark = portfolio.benchmarks.find((e) => e.key === "clientSpecified");
  var selectedBenchmarks = portfolio.benchmarks.filter((e) => e.selected);

  var { benchmarks = [] } = portfolioEntry ? portfolioEntry : portfolio;

  const isCAD = portfolio.currencyType === "CAD";

  const contentCellWidth = isCAD ? 120 : 150;
  const leftCellWidth = isCAD ? 150 : 250;

  const border = "1px solid #cac9c9";

  function getTextCell(benchmark, name, hasBorder) {
    return (
      <div
        style={{
          width: contentCellWidth,
          minWidth: contentCellWidth,
          textAlign: "center",
          paddingLeft: 5,
          paddingRight: 5,
          paddingTop: 15,
          borderLeft: hasBorder ? border : "",
        }}
      >
        {benchmark[name] ? benchmark[name] * 100 + "%" : ""}
      </div>
    );
  }

  function getBenchmarkRow(key) {
    var benchmark = benchmarks.find((e) => e.key === key);

    if (!benchmark) benchmark = {};

    return (
      <div style={{ display: "flex" }}>
        <div
          style={{
            width: leftCellWidth,
            minWidth: leftCellWidth,
            paddingTop: 15,
            paddingLeft: 5,
            paddingRight: 5,
            textAlign: "right",
          }}
        >
          <span style={{ paddingRight: 10 }}>
            {getBenchmarkName(key, portfolio.currencyType)}
          </span>
        </div>
        {getTextCell(benchmark, "mainIndex", true)}
        {getTextCell(benchmark, "secondaryIndex", false)}
        {getTextCell(benchmark, "tenYearBond", false)}
        {getTextCell(benchmark, "oneMonthTbill", false)}
        {isCAD && getTextCell(benchmark, "worldDeveloped", false)}
        {getTextCell(benchmark, "goldETF", false)}
      </div>
    );
  }

  if (benchmarks.length === 0) {
    return <div>No additional benchmarks selected.</div>;
  }

  return (
    <div style={{ width: "100%", fontSize: 14 }}>
      <div style={{ display: "flex" }}>
        <div
          style={{ width: leftCellWidth, minWidth: leftCellWidth, padding: 5 }}
        ></div>
        <div
          style={{
            width: contentCellWidth,
            minWidth: contentCellWidth,
            padding: 5,
            textAlign: "center",
            borderLeft: border,
          }}
        >
          {getBenchmarkColumnName("mainIndex", portfolio.currencyType)}
        </div>
        <div
          style={{
            width: contentCellWidth,
            minWidth: contentCellWidth,
            padding: 5,
            textAlign: "center",
          }}
        >
          {getBenchmarkColumnName("secondaryIndex", portfolio.currencyType)}
        </div>
        <div
          style={{
            width: contentCellWidth,
            minWidth: contentCellWidth,
            padding: 5,
            textAlign: "center",
          }}
        >
          {getBenchmarkColumnName("tenYearBond", portfolio.currencyType)}
        </div>
        <div
          style={{
            width: contentCellWidth,
            minWidth: contentCellWidth,
            padding: 5,
            textAlign: "center",
          }}
        >
          {getBenchmarkColumnName("oneMonthTbill", portfolio.currencyType)}
        </div>
        {isCAD && (
          <div
            style={{
              width: contentCellWidth,
              minWidth: contentCellWidth,
              padding: 5,
              textAlign: "center",
            }}
          >
            {getBenchmarkColumnName("worldDeveloped", portfolio.currencyType)}
          </div>
        )}
        <div
          style={{
            width: contentCellWidth,
            minWidth: contentCellWidth,
            padding: 5,
            textAlign: "center",
          }}
        >
          {getBenchmarkColumnName("goldETF", portfolio.currencyType)}
        </div>
      </div>
      <div style={{ display: "flex" }}>
        <div
          style={{
            width: leftCellWidth,
            minWidth: leftCellWidth,
            paddingTop: 15,
            paddingLeft: 5,
            paddingRight: 5,
            textAlign: "right",
          }}
        >
          <span style={{ paddingRight: 10 }}>Your Benchmark</span>
        </div>
        {getTextCell(benchmark, "mainIndex", true)}
        {getTextCell(benchmark, "secondaryIndex", false)}
        {getTextCell(benchmark, "tenYearBond", false)}
        {getTextCell(benchmark, "oneMonthTbill", false)}
        {isCAD && getTextCell(benchmark, "worldDeveloped", false)}
        {getTextCell(benchmark, "goldETF", false)}
      </div>
      {selectedBenchmarks.find((e) => e.key === "cdnStocks") &&
        getBenchmarkRow("cdnStocks")}
      {selectedBenchmarks.find((e) => e.key === "usStocksUnhedged") &&
        getBenchmarkRow("usStocksUnhedged")}
      {selectedBenchmarks.find((e) => e.key === "cdnLongGovtBonds") &&
        getBenchmarkRow("cdnLongGovtBonds")}
      {selectedBenchmarks.find((e) => e.key === "cdnCash") &&
        getBenchmarkRow("cdnCash")}
      {selectedBenchmarks.find((e) => e.key === "worldDeveloped") &&
        getBenchmarkRow("worldDeveloped")}
      {selectedBenchmarks.find((e) => e.key === "goldETF") &&
        getBenchmarkRow("goldETF")}
      {selectedBenchmarks.find(
        (e) => e.key === "balancedCdnUnhedgedUsEquity",
      ) && getBenchmarkRow("balancedCdnUnhedgedUsEquity")}
      {selectedBenchmarks.find(
        (e) => e.key === "balancedCdnEquityAndCdnGovtBonds",
      ) && getBenchmarkRow("balancedCdnEquityAndCdnGovtBonds")}
      {selectedBenchmarks.find(
        (e) => e.key === "balancedCdnEquityAndCdnGovtBondsPlusCash",
      ) && getBenchmarkRow("balancedCdnEquityAndCdnGovtBondsPlusCash")}
      {selectedBenchmarks.find(
        (e) => e.key === "balancedCdnAndUsEquityAndCdnGovtBonds",
      ) && getBenchmarkRow("balancedCdnAndUsEquityAndCdnGovtBonds")}
    </div>
  );
};

const hasChange = (pp, np) => {
  const a =
    pp.portfolio.benchmarks.filter(
      (e) => e.key !== "clientSpecified" && e.selected,
    ).length !==
    np.portfolio.benchmarks.filter(
      (e) => e.key !== "clientSpecified" && e.selected,
    ).length;

  const b =
    pp.portfolio.addtionalBenchmarksBypass !==
    np.portfolio.addtionalBenchmarksBypass;

  return a || b;
};

export {
  isComplete,
  AdditionalBenchmarks as Render,
  Title,
  Description,
  Preview,
  hasChange,
};



// WEBPACK FOOTER //
// ./src/portfolioEntryWizard/components/AdditionalBenchmarks.js